import React, { useState, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { frFR, enUS } from '@mui/x-date-pickers/locales';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/fr';
import { useTranslation } from 'react-i18next';

import { useAddRaceMutation, useUpdateRaceMutation } from '../api/apiSlice';
import { SPORT_TYPES } from '../constants/sportTypes';
import { useSportTypeLabel } from '../hooks/useSportTypeLabel';
import { RaceDetail } from '../types/api';

interface RaceFormDialogProps {
    race?: RaceDetail;
    onClose: () => void;
    onSuccess?: () => void;
}

interface RaceFormState {
    TITLE: string;
    SPORT_TYPE: string;
    LOCATION: string;
    DESCRIPTION: string;
    REGISTRATION_URL: string;
}

const RaceFormDialog = ({ race, onClose, onSuccess }: RaceFormDialogProps) => {
    const { t, i18n } = useTranslation();
    const sportTypeLabel = useSportTypeLabel();
    const pickersLocaleText = (i18n.language === 'en' ? enUS : frFR).components.MuiLocalizationProvider.defaultProps.localeText;
    const presentationImageInputRef = useRef<HTMLInputElement>(null);
    const rulesPdfInputRef = useRef<HTMLInputElement>(null);
    const [date, setDate] = useState<Dayjs | null>(race ? dayjs(race.DATE) : null);
    const [presentationImage, setPresentationImage] = useState<File | null>(null);
    const [rulesPdf, setRulesPdf] = useState<File | null>(null);
    const [isSelectorOpen, setIsSelectorOpen] = useState(false);
    const [addRace] = useAddRaceMutation();
    const [updateRace] = useUpdateRaceMutation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [form, setForm] = useState<RaceFormState>({
        TITLE: race?.TITLE ?? '',
        SPORT_TYPE: race?.SPORT_TYPE ?? 'Run',
        LOCATION: race?.LOCATION ?? '',
        DESCRIPTION: race?.DESCRIPTION ?? '',
        REGISTRATION_URL: race?.REGISTRATION_URL ?? '',
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        if (!form.TITLE || !form.SPORT_TYPE || !date) return;

        const formData = new FormData();
        formData.append('TITLE', form.TITLE);
        formData.append('SPORT_TYPE', form.SPORT_TYPE);
        formData.append('DATE', date.format('YYYY-MM-DD'));
        formData.append('LOCATION', form.LOCATION);
        formData.append('DESCRIPTION', form.DESCRIPTION);
        formData.append('REGISTRATION_URL', form.REGISTRATION_URL);
        if (presentationImage) formData.append('presentationImage', presentationImage);
        if (rulesPdf) formData.append('rulesPdf', rulesPdf);

        setError(null);
        setIsSubmitting(true);
        try {
            if (race) {
                await updateRace({ raceId: race.ID, formData }).unwrap();
            } else {
                await addRace(formData).unwrap();
            }
            onSuccess?.();
            onClose();
        } catch (err) {
            console.error('error', err);
            const message = (err as { data?: { message?: string } })?.data?.message;
            setError(message ?? t('common.genericError'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="xs">
            <DialogTitle>{race ? t('raceFormDialog.editTitle') : t('raceFormDialog.addTitle')}</DialogTitle>
            <DialogContent>
                {error && (
                    <Box mt={1}>
                        <Alert severity="error">{error}</Alert>
                    </Box>
                )}
                <Box mt={1} mb={1}>
                    <TextField label={t('raceManagement.title')} name="TITLE" value={form.TITLE} onChange={handleInputChange} fullWidth required />
                </Box>
                <Box mb={1}>
                    <FormControl required fullWidth>
                        <InputLabel id="race-sport-type-label">{t('raceFormDialog.sportType')}</InputLabel>
                        <Select
                            labelId="race-sport-type-label"
                            label={t('raceFormDialog.sportType')}
                            name="SPORT_TYPE"
                            value={form.SPORT_TYPE}
                            onChange={handleInputChange}
                            fullWidth
                            open={isSelectorOpen}
                            onClick={() => setIsSelectorOpen(!isSelectorOpen)}
                        >
                            {SPORT_TYPES.map((type) => (
                                <MenuItem key={type} value={type}>{sportTypeLabel(type)}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Box mb={1}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language} localeText={pickersLocaleText}>
                        <DatePicker
                            label={t('raceManagement.date')}
                            format="DD-MM-YYYY"
                            value={date}
                            onChange={(value: Dayjs | null) => setDate(value)}
                            slotProps={{ textField: { fullWidth: true, required: true } }}
                        />
                    </LocalizationProvider>
                </Box>
                <Box mb={1}>
                    <TextField label={t('raceManagement.location')} name="LOCATION" value={form.LOCATION} onChange={handleInputChange} fullWidth />
                </Box>
                <Box mb={1}>
                    <TextField
                        label={t('raceFormDialog.practicalInfo')}
                        name="DESCRIPTION"
                        value={form.DESCRIPTION}
                        onChange={handleInputChange}
                        fullWidth
                        multiline
                        minRows={3}
                    />
                </Box>
                <Box mb={1}>
                    <TextField
                        label={t('raceFormDialog.registrationLink')}
                        name="REGISTRATION_URL"
                        value={form.REGISTRATION_URL}
                        onChange={handleInputChange}
                        fullWidth
                    />
                </Box>
                <Box mb={1} display="flex" gap={1}>
                    <Button variant="contained" color="primary" onClick={() => presentationImageInputRef.current?.click()}>
                        {presentationImage ? presentationImage.name : t('raceFormDialog.presentationImage')}
                    </Button>
                    <Button variant="contained" color="primary" onClick={() => rulesPdfInputRef.current?.click()}>
                        {rulesPdf ? rulesPdf.name : t('races.rulesPdf')}
                    </Button>
                </Box>
                <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    style={{ display: 'none' }}
                    ref={presentationImageInputRef}
                    onChange={(e) => setPresentationImage(e.target.files?.[0] ?? null)}
                />
                <input
                    type="file"
                    accept="application/pdf"
                    style={{ display: 'none' }}
                    ref={rulesPdfInputRef}
                    onChange={(e) => setRulesPdf(e.target.files?.[0] ?? null)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary" disabled={isSubmitting}>
                    {t('common.cancel')}
                </Button>
                <Button
                    onClick={handleSubmit}
                    color="primary"
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : undefined}
                >
                    {race ? t('common.save') : t('common.add')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RaceFormDialog;
